import { IBooking, ILocation, TBookingService, TEmployee } from './store/types'

type ServiceDurationSource = {
    id: string
    hours?: number
    minutes?: number
    duration?: number
}

export const formatBookingServiceDuration = (
    hours: number,
    minutes: number,
): string =>
    `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

export const clampDurationPart = (
    value: number,
    min: number,
    max: number,
): number => Math.min(max, Math.max(min, value))

export const collectServiceIdsFromSchedule = (
    schedule: TEmployee[],
): string[] => {
    const ids = new Set<string>()
    schedule.forEach(row => {
        row.serviceIds?.forEach(id => {
            if (id) {
                ids.add(id)
            }
        })
    })
    return Array.from(ids)
}

export const getLocationImageUrl = (
    location?: Pick<ILocation, 'photoUrl' | 'imageUrl'> | null,
): string => location?.photoUrl || location?.imageUrl || ''

export const countSpecialists = (schedule?: TEmployee[]): number =>
    schedule?.filter(item => Boolean(item.employeeId)).length ?? 0

export const countBookingServiceIds = (
    schedule?: TEmployee[],
    services?: TBookingService[],
): number => {
    const ids = new Set<string>(collectServiceIdsFromSchedule(schedule ?? []))
    services?.forEach(item => {
        if (item.serviceId) {
            ids.add(item.serviceId)
        }
    })
    return ids.size
}

/** Use servicesCount unless the booking lists more services. */
export const getBookingServicesCount = (booking: IBooking): number => {
    const fromDetails = countBookingServiceIds(
        booking.schedule,
        booking.services,
    )
    if (typeof booking.servicesCount === 'number') {
        return Math.max(booking.servicesCount, fromDetails)
    }
    return fromDetails
}

/** Use employeeCount unless the schedule lists more specialists. */
export const getBookingSpecialistsCount = (booking: IBooking): number => {
    const fromSchedule = countSpecialists(booking.schedule)
    if (typeof booking.employeeCount === 'number') {
        return Math.max(booking.employeeCount, fromSchedule)
    }
    return fromSchedule
}

const durationPartsFromSource = (
    source?: Omit<ServiceDurationSource, 'id'>,
): { hours: number; minutes: number } => {
    if (source?.duration != null && source.duration > 0) {
        return {
            hours: Math.floor(source.duration / 60),
            minutes: source.duration % 60,
        }
    }
    const hours = source?.hours ?? 0
    const minutes = source?.minutes ?? 0
    if (hours > 0 || minutes > 0) {
        return { hours, minutes }
    }
    return { hours: 1, minutes: 30 }
}

/**
 * Services the API counts for servicesCount.
 * Unions the services array with serviceIds assigned to specialists.
 */
export const buildBookingServices = (
    schedule: TEmployee[] = [],
    existing: TBookingService[] = [],
    catalog: ServiceDurationSource[] = [],
): TBookingService[] => {
    const ids = new Set<string>()
    existing.forEach(item => {
        if (item.serviceId) {
            ids.add(item.serviceId)
        }
    })
    collectServiceIdsFromSchedule(schedule).forEach(id => ids.add(id))

    return Array.from(ids).map(serviceId => {
        const saved = existing.find(item => item.serviceId === serviceId)
        if (saved) {
            return saved
        }
        const meta = catalog.find(item => item.id === serviceId)
        return { serviceId, ...durationPartsFromSource(meta) }
    })
}

export const getBookingServicesFromBooking = (
    booking: IBooking,
): TBookingService[] => {
    if (booking.services?.length) {
        return booking.services
    }
    return buildBookingServices(booking.schedule ?? [])
}
